/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * The reducer of Expenses Analyzer component
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.1.0
 */
import produce from 'immer';
import { Reducer } from 'redux';

import { IChartCategoryRange, IChartDataInput, IChartLabels } from '../../components/common/Chart/chartTypes';
import { LOGOUT_START_SUCCESSFUL } from '../PageAuth/constants/authConstants';
import { EExpensesAnalyzerChart } from './components/ExpensesAnalyzerController/expensesAnalyzerControllerTypes';
import {
    EXPENSES_ANALYZER_FULLED_ACCOUNTS_LIST,
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST,
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_FAILED,
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_SUCCESS,
    EXPENSES_ANALYZER_INITIALIZE_CHART,
    EXPENSES_ANALYZER_LOAD_CHART,
    EXPENSES_ANALYZER_LOAD_CHART_ERROR,
    EXPENSES_ANALYZER_LOAD_CHART_SUCCESSFUL,
    EXPENSES_ANALYZER_SET_CURRENT_ACCOUNT,
    EXPENSES_ANALYZER_SET_CURRENT_CHART,
    EXPENSES_ANALYZER_SET_SELECTED_ID,
    EXPENSES_ANALYZER_SHOW_GRAPH,
    EXPENSES_ANALYZER_UPDATE_CHART_ITEM_SUCCESSFUL,
    SET_EXPENSES_ANALYZER_LOADING_CHART,
    SET_EXPENSES_ANALYZER_TYPE,
} from './pageExpensesAnalyzerConstants';
import { TExpensesAnalyzerTableTypesAction } from './types/pageExpensesAnalyzerActionTypes';
import { EExpensesAnalyzerTypes, IExpensesAccountsListItem } from './types/pageExpensesAnalyzerTypes';

export interface IPageExpensesAnalyzerReducer {
    accountsList: IExpensesAccountsListItem[];
    accountsListFulled: boolean;
    currentAccount: number;
    chartCategoryRange: IChartCategoryRange;
    currentChart: EExpensesAnalyzerChart;
    chartData: {
        [EExpensesAnalyzerChart.CATEGORY]: IChartDataInput[];
        [EExpensesAnalyzerChart.DYNAMIC]: IChartDataInput[];
    };
    chartLabels: IChartLabels;
    error: string;
    expensesAnalyzerType: EExpensesAnalyzerTypes;
    isChartLoading: boolean;
    isShowGraph: boolean;
    selectedId: number;
}

export const initStateExpensesAnalyzer: IPageExpensesAnalyzerReducer = {
    accountsList: [],
    accountsListFulled: false,
    chartCategoryRange: {
        from: '',
        to: '',
    },
    chartData: {
        [EExpensesAnalyzerChart.CATEGORY]: [],
        [EExpensesAnalyzerChart.DYNAMIC]: [],
    },
    chartLabels: {},
    currentAccount: 0,
    currentChart: EExpensesAnalyzerChart.CATEGORY,
    expensesAnalyzerType: EExpensesAnalyzerTypes.Account,
    error: '',
    isChartLoading: false,
    isShowGraph: true,
    selectedId: 0,
};

const expensesAnalyzerPageReducer: Reducer<IPageExpensesAnalyzerReducer, TExpensesAnalyzerTableTypesAction> =
    (state: IPageExpensesAnalyzerReducer = initStateExpensesAnalyzer, action: TExpensesAnalyzerTableTypesAction) =>
        produce(state, draft => {
            switch (action.type) {
                case EXPENSES_ANALYZER_SHOW_GRAPH:
                    draft.isShowGraph = action.payload;
                    break;

                case EXPENSES_ANALYZER_SET_CURRENT_ACCOUNT:
                    draft.currentAccount = action.payload;
                    break;

                case EXPENSES_ANALYZER_SET_SELECTED_ID:
                    draft.selectedId = action.payload;
                    break;

                case EXPENSES_ANALYZER_SET_CURRENT_CHART:
                    draft.currentChart = action.payload;
                    break;

                case SET_EXPENSES_ANALYZER_LOADING_CHART:
                    draft.isChartLoading = action.payload;
                    break;

                case EXPENSES_ANALYZER_LOAD_CHART:
                    draft.isChartLoading = true;
                    break;
                case EXPENSES_ANALYZER_LOAD_CHART_SUCCESSFUL:
                    if (action.payload.chartCategoryRange) {
                        draft.chartCategoryRange.from = action.payload.chartCategoryRange.from;
                        draft.chartCategoryRange.to = action.payload.chartCategoryRange.to;
                    }
                    draft.isChartLoading = false;
                    draft.chartData[action.payload.type] = action.payload.data;
                    draft.chartLabels = action.payload.labels;
                    draft.error = '';
                    break;
                case EXPENSES_ANALYZER_LOAD_CHART_ERROR:
                    draft.isChartLoading = false;
                    draft.error = action.payload;
                    break;

                case EXPENSES_ANALYZER_INITIALIZE_CHART:
                    draft.isChartLoading = true;
                    break;

                case EXPENSES_ANALYZER_UPDATE_CHART_ITEM_SUCCESSFUL:
                    draft.chartData[draft.currentChart] = action.payload;
                    break;

                case SET_EXPENSES_ANALYZER_TYPE:
                    draft.expensesAnalyzerType = action.payload;
                    break;

                /*
                * -------------------------------------------
                *        GET ACCOUNTS LIST
                * -------------------------------------------
                */

                case EXPENSES_ANALYZER_FULLED_ACCOUNTS_LIST:
                    draft.accountsListFulled = true;
                    break;

                case EXPENSES_ANALYZER_GET_ACCOUNTS_LIST:
                    if (action.payload) {
                        draft.accountsListFulled = false;
                        draft.accountsList = [];
                    }
                    break;

                case EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_SUCCESS:
                    draft.accountsList = action.payload;
                    break;

                case EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_FAILED:
                    draft.error = action.payload;
                    break;

                case LOGOUT_START_SUCCESSFUL:
                    return initStateExpensesAnalyzer;

                default:
                    break;
            }
        });

export default expensesAnalyzerPageReducer;
